/**
 * Rows for the cart's two distinct kinds of content, which look similar but are
 * semantically different things:
 *
 * - `CartItem` — a product in the cart. Rendered inside an `<li>`, since the
 *   cart's contents are a list.
 * - `SummaryRow` — one Subtotal / Shipping / Taxes / Total figure. Rendered as a
 *   `<dt>`/`<dd>` pair inside a `<dl>`, because those are literally name-value
 *   pairs, and a description list is the element for that.
 *
 * Both share the money formatting in `renderValue` below.
 */
import React from 'react';
import { Trash } from './theme/icons.js';
import styled from './theme/styled.js';
import { token } from './theme/tokens.js';
import { QuantityInput } from './elements/QuantityInputEmbedded.js';
import { DeleteButton } from './cartPanelStyles.js';
import { Money } from './elements/Money.js';
import InlineList from './elements/InlineList.js';

const Item = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: token(theme, 'space.mediumGap'),
}));

const ItemContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: 6,
});

const ImageWrapper = styled.div(({ theme }) => ({
    flexShrink: 0,
    width: token(theme, 'size.itemImage'),
    height: token(theme, 'size.itemImage'),
    img: {
        objectFit: 'contain',
    },
    border: `solid 1px ${token(theme, 'color.borderFooter')}`,
    borderRadius: token(theme, 'radius.button'),
}));

const ItemImage = styled.img(() => ({
    height: '100%',
    width: '100%',
}));

const ItemInfoRow = styled.div(({ theme, isBold }) => ({
    fontSize: token(theme, 'fontSize.body'),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: isBold ? token(theme, 'font.bold') : token(theme, 'font.body'),
    ...isBold ? { fontWeight: 'bold' } : {}, // TODO: load in font faces too?

}));

const DiscountItemInfoRow = styled(ItemInfoRow)({
    fontSize: '13px',
});

const QuantityAndAmounts = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 6,
});

const Amounts = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
});

const Title = styled.div(({ theme }) => ({
    fontFamily: token(theme, 'font.body'),
    fontWeight: 'bold',
}));

const SubscriptionInfo = styled.div(({ theme }) => ({
    borderRadius: token(theme, 'radius.description'),
    backgroundColor: token(theme, 'color.detailCalloutBackground'),
    padding: `${token(theme, 'space.tinyGap')} ${token(theme, 'space.smallGap')}`,
}));

const UnitPrice = styled.div(({ theme }) => ({
    fontFamily: token(theme, 'font.body'),
    fontSize: token(theme, 'fontSize.small'),
}));

const Subtotal = styled(UnitPrice)({
    fontWeight: 'bold',
});

export const CartItem = ({
    isLoading,
    onQuantityChange,
    onRemoveProduct,

    hasValidPricing,
    lineItem = {},
}) => {
    const hasImage = Boolean(lineItem.product.imageUrl);

    return <Item>
        <ImageWrapper>
            {hasImage && <ItemImage alt={lineItem.product.title} src={lineItem.product.imageUrl} />}
        </ImageWrapper>
        <ItemContent>
            <ItemInfoRow>
                <Title>{lineItem.product.title}</Title>
                <DeleteButton
                    type="button"
                    title="Remove item"
                    aria-label={`Remove ${lineItem.product.title} from cart`}
                    onClick={() => onRemoveProduct(lineItem.product.productCode, lineItem.item.selection)}
                >
                    <Trash />
                </DeleteButton>
            </ItemInfoRow>
            {lineItem.product.tags?.length > 0 && <InlineList items={lineItem.product.tags} />}
            {Object.values(lineItem.item.selection || {}).length > 0 && <InlineList items={Object.values(lineItem.item.selection)} />}
            {lineItem.subscription?.required && <SubscriptionInfo>
                <InlineList items={['Plan required', <><Money
                    hasValidPricing={hasValidPricing}
                    currencySymbol={lineItem.item.currencySymbol}
                    currencyCode={lineItem.item.currencyCode}
                    value={lineItem.subscription.proratedAmount}
                />{' today, then '}<Money
                    hasValidPricing={hasValidPricing}
                    currencySymbol={lineItem.item.currencySymbol}
                    currencyCode={lineItem.item.currencyCode}
                    value={lineItem.subscription.unitPrice}
                />{`/${lineItem.subscription.interval}`}</>]} />
            </SubscriptionInfo>}
            {lineItem.item.discounts?.map((discount, i) => // TODO: discounts in the cart?
                <DiscountItemInfoRow key={i}>
                    <Title>{discount.description}</Title>

                    <UnitPrice>
                        <Money
                            hasValidPricing={hasValidPricing}
                            currencySymbol={lineItem.item.currencySymbol}
                            currencyCode={lineItem.item.currencyCode}
                            value={-1 * discount.subtotal}
                        />
                    </UnitPrice>
                </DiscountItemInfoRow>,
            )}
            <QuantityAndAmounts>
                <QuantityInput
                    productCode={lineItem.product.productCode}
                    value={lineItem.item.quantity}
                    onChange={(newQty) => onQuantityChange(lineItem.product.productCode, lineItem.item.selection, newQty)}
                    isLoading={isLoading}
                    minValue={1}
                    itemLabel={lineItem.product.title}
                />
                <Amounts>
                    <Subtotal>
                        <Money
                            hasValidPricing={hasValidPricing}
                            currencySymbol={lineItem.item.currencySymbol}
                            currencyCode={lineItem.item.currencyCode}
                            value={lineItem.item.subtotal}
                        />
                    </Subtotal>
                    <UnitPrice>
                        <Money
                            hasValidPricing={hasValidPricing}
                            currencySymbol={lineItem.item.currencySymbol}
                            currencyCode={lineItem.item.currencyCode}
                            value={lineItem.item.unitPrice}
                        /> each
                    </UnitPrice>
                </Amounts>
            </QuantityAndAmounts>
        </ItemContent>
    </Item>;
};

